import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { dateToUtc } from '../../../functions';
import { RootState } from '../../../redux/rootReducer';
import { getDateDaysFromNow } from '../../fuctions';
import { ProjectStatus } from '../../interfaces';
import { getJobs } from '../api';
import { AdminJob, GeneralJob } from '../interfaces';

const initialState = {
  startDate: getDateDaysFromNow(-14),
  endDate: getDateDaysFromNow(1),
  search: '',
  page: 1,
  pageSize: 25,
  status: ProjectStatus.ALL,
  projectCode: '',
  account: '',
  areFiltersChanged: false,
  loadingJobs: false,
  jobsList: [],
  jobCount: 0,
  jobPages: 0,
  addJobModalOpen: false,
};

export default function useJobHistory(isAdmin: boolean) {
  const userData: any = useSelector(
    (state: RootState) => state?.mainAppData?.userData,
  );
  const [startDate, setStartDate] = useState<Date | null>(
    initialState.startDate,
  );
  const [endDate, setEndDate] = useState<Date | null>(initialState.endDate);
  const [search, setSearch] = useState<string>(initialState.search);
  const [page, setPage] = useState<number>(initialState.page);
  const [pageSize, setPageSize] = useState<number>(initialState.pageSize);
  const [status, setStatus] = useState<ProjectStatus>(initialState.status);
  const [projectCode, setProjectCode] = useState<string>(
    initialState.projectCode,
  );
  const [account, setAccount] = useState<string>(
    userData?.account_number || initialState.account,
  );
  const [areFiltersChanged, setAreFiltersChanged] = useState<boolean>(
    initialState.areFiltersChanged,
  );
  const [loadingJobs, setLoadingJobs] = useState<boolean>(
    initialState.loadingJobs,
  );
  const [jobsList, setJobsList] = useState<GeneralJob[] | AdminJob[]>(
    initialState.jobsList,
  );
  const [jobCount, setJobCount] = useState<number>(initialState.jobCount);
  const [jobPages, setJobPages] = useState<number>(initialState.jobPages);
  const [addJobModalOpen, setAddJobModalOpen] = useState<boolean>(
    initialState.addJobModalOpen,
  );

  const curPayload = {
    search,
    page,
    pageSize,
    startDate,
    endDate,
    status,
    projectCode,
    account: isAdmin ? account : undefined,
  };

  const fetchJobs = async (payload: any) => {
    const { jobs, count, pages } = await getJobs(
      payload,
      isAdmin,
      setLoadingJobs,
    );
    setJobsList(jobs);
    setJobCount(count);
    setJobPages(pages);
  };

  const onChangeDates = (dates: [Date | null, Date | null]) => {
    const [start, end] = dates;
    setStartDate(start);
    setEndDate(end);
    setAreFiltersChanged(true);
  };

  const onChangeRawDates = (event: React.FocusEvent<HTMLInputElement>) => {
    const input = event.target.value;
    if (!input) return;

    const [start, end] = input.split(' - ').map((date: string) => {
      const parsedDate = dateToUtc(new Date(date.trim()));
      if (!parsedDate) {
        return null;
      }
      return parsedDate;
    });
    if (start && end) {
      setStartDate(start);
      setEndDate(end);
      setAreFiltersChanged(true);
    }
  };

  const onChangeProjectCode = (newProjectCode: string) => {
    setProjectCode(newProjectCode);
    setAreFiltersChanged(true);
  };

  const onChangeStatus = (newStatus: ProjectStatus) => {
    setStatus(newStatus);
    setAreFiltersChanged(true);
  };

  const onChangeAccount = (newAccount: string) => {
    setAccount(newAccount);
    setAreFiltersChanged(true);
  };

  const onClickFilter = () => {
    setAreFiltersChanged(false);
    fetchJobs(curPayload);
  };

  const onChangeSearch = (newSearch: string) => {
    setSearch(newSearch);
    fetchJobs({ ...curPayload, search: newSearch });
  };

  const onChangePage = (newPage: number) => {
    setPage(newPage);
    fetchJobs({ ...curPayload, page: newPage });
  };

  const onChangePageSize = (newPageSize: number) => {
    setPageSize(newPageSize);
    fetchJobs({ ...curPayload, pageSize: newPageSize });
  };

  const onClickAdd = () => {
    setAddJobModalOpen(true);
  };

  const onCloseAddJobModal = () => {
    setAddJobModalOpen(false);
  };

  const onAddJobSuccess = () => {
    setAddJobModalOpen(false);
    fetchJobs(curPayload);
  };

  useEffect(() => {
    setStartDate(initialState.startDate);
    setEndDate(initialState.endDate);
    setSearch(initialState.search);
    setPage(initialState.page);
    setPageSize(initialState.pageSize);
    setStatus(initialState.status);
    setProjectCode(initialState.projectCode);
    setAccount(userData?.account_number || initialState.account);
    setAreFiltersChanged(initialState.areFiltersChanged);
    setLoadingJobs(initialState.loadingJobs);
    setJobsList(initialState.jobsList);
    setJobCount(initialState.jobCount);
    setJobPages(initialState.jobPages);
    setAddJobModalOpen(initialState.addJobModalOpen);

    fetchJobs(initialState);
  }, [isAdmin]);

  return {
    startDate,
    endDate,
    onChangeDates,
    onChangeRawDates,
    account,
    onChangeAccount,
    status,
    onChangeStatus,
    projectCode,
    onChangeProjectCode,
    areFiltersChanged,
    onClickFilter,
    search,
    onChangeSearch,
    onClickAdd,
    addJobModalOpen,
    onCloseAddJobModal,
    onAddJobSuccess,
    jobsList,
    loadingJobs,
    page,
    onChangePage,
    setPage,
    pageSize,
    onChangePageSize,
    setPageSize,
    jobCount,
    jobPages,
  };
}
